import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { resolveAskArifRedirect } from "@/lib/redirects";
import { motion } from "motion/react";
import { ArrowUpRight, Clock, Calendar, Eye, Sparkles, GraduationCap, HelpCircle, Folder } from "lucide-react";
import { AuthorCard } from "@/components/AuthorCard";
import {
  getArticle,
  articleMetaTitle,
  getCategory,
  relatedArticles,
  ASK_ARIF_ARTICLES,
} from "@/lib/askArif";
import portraitOg from "@/assets/portrait-thinking.webp";
import { CATEGORY_OG, ogUrl } from "@/lib/ogSections";
import { ASK_ARIF_OG_CARDS } from "@/lib/ogCards";

import { seoDescription, titleWithSuffix } from "@/lib/seoText";
import { pageSchemaScript, PERSON_ID } from "@/seo/schema";

const SITE_ORIGIN = "https://arifkhanglobal.com";

// Standard OG image dimensions (matches generator output and platform recommendations).
const OG_IMAGE_WIDTH = 1216;
const OG_IMAGE_HEIGHT = 640;

// Auto-discover per-slug OG creatives. Drop a `{slug}.{jpg,png,webp}` file into
// `src/assets/og-ask-arif/` and it is wired automatically — no code changes required.
// The generator at `scripts/generate-og-images.mjs` produces files with this exact
// naming convention.
const ogFileModules = import.meta.glob<{ default: string }>(
  "@/assets/og-ask-arif/*.{jpg,jpeg,png,webp}",
  { eager: true },
);
const OG_IMAGE_BY_SLUG: Record<string, string> = Object.fromEntries(
  Object.entries(ogFileModules).map(([path, mod]) => {
    const name = path.split("/").pop() ?? "";
    const slug = name.replace(/\.(jpe?g|png|webp)$/i, "");
    return [slug, mod.default];
  }),
);

function resolveOgImage(slug: string, categorySlug: string): string {
  // Priority: the per-article branded card, then legacy bespoke art,
  // then the category creative, then the portrait fallback.
  return (
    ASK_ARIF_OG_CARDS[slug] ??
    OG_IMAGE_BY_SLUG[slug] ??
    CATEGORY_OG[categorySlug] ??
    portraitOg
  );
}


export const Route = createFileRoute("/ask-arif/$slug")({
  loader: ({ params }) => {
    // 301 known legacy slugs to their canonical current slug.
    const canonical = resolveAskArifRedirect(params.slug);
    if (canonical) {
      throw redirect({
        to: "/ask-arif/$slug",
        params: { slug: canonical },
        statusCode: 301,
      });
    }
    const article = getArticle(params.slug);
    if (!article) throw notFound();
    return { article };
  },
  head: ({ loaderData, params }) => {
    const a = loaderData?.article;
    if (!a) return {};
    const title = titleWithSuffix(articleMetaTitle(a), "| Ask Arif");
    const description = seoDescription(a.shortAnswer);
    const url = `${SITE_ORIGIN}/ask-arif/${params.slug}`;
    const ogImage = ogUrl(resolveOgImage(params.slug, a.category));
    const ogImageAlt = `${a.question} — Ask Arif by Arif Khan`;
    const category = getCategory(a.category);
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { property: "og:image", content: ogImage },
        { property: "og:image:secure_url", content: ogImage },
        { property: "og:image:type", content: "image/jpeg" },
        { property: "og:image:width", content: String(OG_IMAGE_WIDTH) },
        { property: "og:image:height", content: String(OG_IMAGE_HEIGHT) },
        { property: "og:image:alt", content: ogImageAlt },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: ogImage },
        { name: "twitter:image:alt", content: ogImageAlt },
        { property: "article:author", content: "Arif Khan" },
        { property: "article:published_time", content: a.publishedAt },
      ],
      links: [
        { rel: "canonical", href: url },
        { rel: "alternate", type: "text/markdown", href: `${url}.md`, title: "Markdown mirror" },
      ],
      scripts: [
        pageSchemaScript(
          {
            path: `/ask-arif/${params.slug}`,
            type: "WebPage",
            name: title,
            description,
            breadcrumb: [
              { name: "Ask Arif", url: "/ask-arif" },
              ...(category ? [{ name: category.title, url: `/ask-arif/category/${category.slug}` }] : []),
              { name: a.question, url: `/ask-arif/${params.slug}` },
            ],
            extra: { mainEntity: { "@id": `${url}#article` } },
          },
          [
            {
              "@type": "Article",
              "@id": `${url}#article`,
              headline: a.question,
              description: a.shortAnswer,
              url,
              image: {
                "@type": "ImageObject",
                url: ogImage,
                width: OG_IMAGE_WIDTH,
                height: OG_IMAGE_HEIGHT,
                caption: ogImageAlt,
              },
              datePublished: a.publishedAt,
              dateModified: a.publishedAt,
              inLanguage: "en-IN",
              author: { "@id": PERSON_ID },
              publisher: { "@id": PERSON_ID },
              mainEntityOfPage: { "@id": `${url}#webpage` },
              speakable: {
                "@type": "SpeakableSpecification",
                cssSelector: ["[data-short-answer]"],
              },
              keywords: a.tags.join(", "),
              articleSection: category?.title,
            },
            {
              "@type": "FAQPage",
              "@id": `${url}#faq`,
              inLanguage: "en-IN",
              isPartOf: { "@id": `${url}#webpage` },
              mainEntity: [
                { "@type": "Question", name: a.question, acceptedAnswer: { "@type": "Answer", text: a.shortAnswer } },
                ...a.faq.map((f) => ({
                  "@type": "Question",
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              ],
            },
          ],
        ),
      ],
    };
  },
  notFoundComponent: () => (
    <div className="container-page py-40 text-center">
      <h1 className="text-3xl font-semibold">Question not found</h1>
      <Link to="/ask-arif" className="mt-6 inline-flex text-sm font-medium text-primary">
        Back to Ask Arif
      </Link>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="container-page py-40 text-center">
      <h1 className="text-2xl font-semibold">This article didn't load</h1>
      <p className="mt-2 text-sm text-ink-soft">{error.message}</p>
    </div>
  ),
  component: ArticlePage,
});

function ArticlePage() {
  const data = Route.useLoaderData() as { article: import("@/lib/askArif").AskArifArticle };
  const article = data.article;
  const category = getCategory(article.category);
  const related = relatedArticles(article.slug, 3);


  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border bg-surface pt-36 pb-20">
        <div aria-hidden className="absolute inset-0 grain pointer-events-none" />
        <div
          aria-hidden
          className="absolute -top-32 left-1/3 h-[480px] w-[820px] rounded-full bg-gradient-to-br from-primary/15 via-accent/10 to-transparent blur-3xl"
        />
        <div className="container-page relative max-w-4xl">
          <div className="flex flex-wrap items-center gap-3 text-xs text-ink-soft">
            <Link to="/ask-arif" className="hover:text-primary transition-colors">
              Ask Arif
            </Link>
            <span>/</span>
            {category && (
              <Link
                to="/ask-arif/category/$category"
                params={{ category: category.slug }}
                className="hover:text-primary transition-colors"
              >
                {category.title}
              </Link>
            )}
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mt-6 text-balance text-4xl md:text-6xl font-semibold tracking-tight leading-[1.05]"
          >
            {article.question}
          </motion.h1>
          <div className="mt-8 flex flex-wrap items-center gap-5 text-xs text-ink-soft">
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" strokeWidth={1.8} />
              {article.readingMinutes} min read
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" strokeWidth={1.8} />
              {new Date(article.publishedAt).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Eye className="h-3.5 w-3.5" strokeWidth={1.8} />
              By Arif Khan
            </span>
          </div>
          <div className="mt-6 flex flex-wrap gap-2">
            {article.tags.map((t) => (
              <span key={t} className="text-[11px] font-medium px-3 py-1 rounded-full border border-border bg-background text-ink-soft">
                {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Short answer — citation-ready block */}
      <section id="short-answer" className="scroll-mt-28 py-20 border-b border-border">
        <div className="container-page max-w-3xl">
          <div className="rounded-3xl border border-primary/25 bg-primary/[0.04] p-8 md:p-10">
            <div className="flex items-center gap-2">
              <HelpCircle className="h-4 w-4 text-primary" strokeWidth={1.8} />
              <span className="eyebrow text-primary">Short answer</span>
            </div>
            <h2 className="sr-only">Short answer: {article.question}</h2>
            <p
              data-short-answer
              className="mt-5 text-xl md:text-2xl text-foreground leading-relaxed text-pretty"
            >
              {article.shortAnswer}
            </p>
            <p className="mt-6 text-xs text-ink-soft">
              Answered by Arif Khan ·{" "}
              {new Date(article.publishedAt).toLocaleDateString("en-US", {
                month: "short",
                year: "numeric",
              })}{" "}
              · <a href="#faq" className="text-primary hover:underline underline-offset-4">Detailed FAQ below</a>
            </p>
          </div>
        </div>
      </section>


      {/* Visual explanation */}
      <section className="py-20 border-b border-border bg-surface">
        <div className="container-page max-w-4xl">
          <span className="eyebrow">Visual Explanation</span>
          <h2 className="mt-3 text-2xl md:text-3xl font-semibold tracking-tight">How to picture it.</h2>
          <div className="mt-8 glass border border-white/40 rounded-3xl p-8 md:p-12 shadow-[0_30px_80px_-40px_rgba(21,101,192,0.4)]">
            <div className="flex items-start gap-4">
              <div className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Sparkles className="h-5 w-5" strokeWidth={1.6} />
              </div>
              <p className="text-base md:text-lg text-ink-soft leading-relaxed">{article.visual}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Real-world example */}
      <section className="py-20 border-b border-border">
        <div className="container-page max-w-3xl">
          <span className="eyebrow">Real-World Example</span>
          <h2 className="mt-3 text-2xl md:text-3xl font-semibold tracking-tight">From the field.</h2>
          <p className="mt-6 text-base md:text-lg text-ink-soft leading-relaxed">{article.example}</p>
        </div>
      </section>

      {/* Lessons learned */}
      <section className="py-20 border-b border-border bg-surface">
        <div className="container-page max-w-3xl">
          <span className="eyebrow">Lessons Learned</span>
          <h2 className="mt-3 text-2xl md:text-3xl font-semibold tracking-tight flex items-center gap-3">
            <GraduationCap className="h-7 w-7 text-primary" strokeWidth={1.6} />
            What to take away.
          </h2>
          <ul className="mt-8 grid gap-3">
            {article.lessons.map((l, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="flex gap-4 rounded-xl border border-border bg-background p-5"
              >
                <span className="shrink-0 h-7 w-7 rounded-full bg-primary/10 text-primary text-xs font-semibold inline-flex items-center justify-center">
                  {i + 1}
                </span>
                <span className="text-sm md:text-base text-foreground leading-relaxed">{l}</span>
              </motion.li>
            ))}
          </ul>
        </div>
      </section>

      {/* Related projects */}
      {article.relatedProjects.length > 0 && (
        <section className="py-20 border-b border-border">
          <div className="container-page max-w-4xl">
            <span className="eyebrow">Related Projects</span>
            <h2 className="mt-3 text-2xl md:text-3xl font-semibold tracking-tight flex items-center gap-3">
              <Folder className="h-7 w-7 text-primary" strokeWidth={1.6} />
              Where this thinking shipped.
            </h2>
            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              {article.relatedProjects.map((p) => (
                <div key={p} className="rounded-2xl border border-border bg-background p-6">
                  <p className="text-sm font-medium text-foreground">{p}</p>
                  <Link
                    to="/projects"
                    className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-primary"
                  >
                    See projects <ArrowUpRight className="h-3 w-3" />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section id="faq" className="scroll-mt-28 py-20 border-b border-border bg-surface">
        <div className="container-page max-w-3xl">
          <span className="eyebrow">FAQ</span>
          <h2 className="mt-3 text-2xl md:text-3xl font-semibold tracking-tight flex items-center gap-3">
            <HelpCircle className="h-7 w-7 text-primary" strokeWidth={1.6} />
            Quick follow-ups.
          </h2>
          <div className="mt-8 grid gap-3">
            {article.faq.map((f, i) => (
              <details
                key={i}
                className="group rounded-2xl border border-border bg-background p-5 open:border-primary/40 transition-colors"
              >
                <summary className="cursor-pointer list-none flex items-center justify-between gap-4 text-base font-medium">
                  {f.q}
                  <span className="text-primary text-xl leading-none transition-transform group-open:rotate-45">
                    +
                  </span>
                </summary>
                <p className="mt-3 text-sm text-ink-soft leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Related articles */}
      {related.length > 0 && (
        <section className="py-20 border-b border-border">
          <div className="container-page">
            <span className="eyebrow">Related Articles</span>
            <h2 className="mt-3 text-2xl md:text-3xl font-semibold tracking-tight">Keep reading.</h2>
            <div className="mt-10 grid gap-6 md:grid-cols-3">
              {related.map((r) => {
                const rc = getCategory(r.category);
                return (
                  <Link
                    key={r.slug}
                    to="/ask-arif/$slug"
                    params={{ slug: r.slug }}
                    className="group flex h-full flex-col rounded-2xl border border-border bg-background p-6 hover:border-primary/40 hover:shadow-[0_20px_60px_-30px_rgba(21,101,192,0.35)] transition-all"
                  >
                    {rc && (
                      <div className="text-[11px] font-medium uppercase tracking-widest text-primary">
                        {rc.title}
                      </div>
                    )}
                    <h3 className="mt-3 text-base font-semibold tracking-tight">{r.question}</h3>
                    <div className="mt-auto pt-6 flex items-center justify-between text-[11px] text-ink-soft">
                      <span className="inline-flex items-center gap-1.5">
                        <Clock className="h-3 w-3" strokeWidth={1.8} />
                        {r.readingMinutes} min
                      </span>
                      <ArrowUpRight className="h-4 w-4 group-hover:text-primary transition-colors" />
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* About Arif */}
      <section className="py-20 border-b border-border bg-surface">
        <div className="container-page max-w-4xl">
          <AuthorCard />
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="container-page max-w-3xl text-center">
          <span className="eyebrow text-primary">Have a project in mind?</span>
          <h2 className="mt-4 text-3xl md:text-5xl font-semibold tracking-tight text-balance">
            Talk to Arif directly.
          </h2>
          <p className="mt-5 text-base md:text-lg text-ink-soft">
            From a single private theatre to a multi-site security programme — get an honest, experienced read.
          </p>
          <Link
            to="/contact"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:opacity-90 transition-opacity"
          >
            Let's Talk
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </>
  );
}

void ASK_ARIF_ARTICLES;
